/**
 * Shared chart infrastructure.
 *
 * Conventions every chart in this kit follows:
 *  - It measures its own container and draws to real pixels. No fixed widths,
 *    no viewBox scaling that would make a 10px label render at 7px.
 *  - It takes `height` but never `width`.
 *  - Colour is always a CSS custom property name (`--w7-c1`), never a literal,
 *    so a theme change moves every chart at once.
 *  - It renders `role="img"` with an `aria-label` summarising the data, because
 *    an SVG of paths is invisible to a screen reader otherwise.
 *  - Every chart has a designed empty state. A chart with no data must say so,
 *    not draw an empty box.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/* ------------------------------------------------------------- measuring -- */

export interface Size {
  width: number
  height: number
}

/**
 * Observes an element's size. Returns 0 until the first measurement, and every
 * chart treats width 0 as "not ready" rather than drawing a degenerate frame.
 */
export function useSize<T extends HTMLElement>(): [React.RefObject<T | null>, Size] {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => {
      const rect = el.getBoundingClientRect()
      setSize((prev) =>
        Math.abs(prev.width - rect.width) < 0.5 && Math.abs(prev.height - rect.height) < 0.5
          ? prev
          : { width: rect.width, height: rect.height },
      )
    }

    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return [ref, size]
}

/* -------------------------------------------------------------- palette --- */

export const SERIES_VARS = [
  '--w7-c1',
  '--w7-c2',
  '--w7-c3',
  '--w7-c4',
  '--w7-c5',
  '--w7-c6',
  '--w7-c7',
  '--w7-c8',
] as const

/** Cycles the categorical palette. Beyond 8 series, add a legend — not a hue. */
export const seriesColor = (index: number): string =>
  `var(${SERIES_VARS[index % SERIES_VARS.length]})`

/** The six-step sequential ramp, for heat encodings. */
export const seqColor = (t: number): string => {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0))
  const step = Math.round(clamped * 5)
  return `var(--w7-seq-${step})`
}

/** Money tone as a CSS colour — the one place charts decide green vs red. */
export const toneColor = (value: number): string =>
  value > 0 ? 'var(--w7-profit)' : value < 0 ? 'var(--w7-loss)' : 'var(--w7-text-3)'

/* --------------------------------------------------------------- tooltip -- */

export interface TooltipRow {
  label: string
  value: string
  color?: string
}

export interface TooltipState {
  x: number
  y: number
  title: string
  rows: TooltipRow[]
}

interface TooltipApi {
  show(state: TooltipState): void
  hide(): void
}

const TooltipContext = createContext<TooltipApi | null>(null)

/**
 * One tooltip element for the whole page, positioned fixed. Charts push content
 * into it rather than each rendering their own absolutely-positioned div, which
 * keeps them out of overflow-hidden traps inside cards.
 */
export function ChartTooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TooltipState | null>(null)
  const api = useMemo<TooltipApi>(
    () => ({ show: setState, hide: () => setState(null) }),
    [],
  )

  return (
    <TooltipContext.Provider value={api}>
      {children}
      {state ? <FloatingTooltip {...state} /> : null}
    </TooltipContext.Provider>
  )
}

function FloatingTooltip({ x, y, title, rows }: TooltipState) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x + 14, top: y + 14 })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const pad = 8
    let left = x + 14
    let top = y + 14
    if (left + rect.width > window.innerWidth - pad) left = x - rect.width - 14
    if (top + rect.height > window.innerHeight - pad) top = y - rect.height - 14
    setPos({ left: Math.max(pad, left), top: Math.max(pad, top) })
  }, [x, y, title, rows])

  return (
    <div ref={ref} className="w7-tooltip" style={pos} role="status">
      <div style={{ fontWeight: 600, marginBottom: rows.length ? 4 : 0 }}>{title}</div>
      {rows.map((row, i) => (
        <div
          key={`${row.label}-${i}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
        >
          {row.color ? (
            <span className="w7-legend-swatch" style={{ background: row.color }} />
          ) : null}
          <span style={{ color: 'var(--w7-text-3)' }}>{row.label}</span>
          <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

export function useChartTooltip(): TooltipApi {
  // A chart used outside the provider must degrade to no tooltip, not crash.
  return useContext(TooltipContext) ?? NOOP_TOOLTIP
}

const NOOP_TOOLTIP: TooltipApi = { show: () => {}, hide: () => {} }

/* ----------------------------------------------------------------- frame -- */

export interface Margin {
  top: number
  right: number
  bottom: number
  left: number
}

export const MARGIN: Margin = { top: 8, right: 8, bottom: 22, left: 48 }

export interface Plot {
  width: number
  height: number
  inner: { width: number; height: number }
  margin: Margin
}

export function plotArea(size: Size, height: number, margin: Partial<Margin> = {}): Plot {
  const m = { ...MARGIN, ...margin }
  return {
    width: size.width,
    height,
    inner: {
      width: Math.max(0, size.width - m.left - m.right),
      height: Math.max(0, height - m.top - m.bottom),
    },
    margin: m,
  }
}

/* ------------------------------------------------------------ chart shell -- */

export interface ChartShellProps {
  height: number
  label: string
  /** Rendered instead of the chart when there is nothing to draw. */
  empty?: ReactNode
  isEmpty?: boolean
  children: (plot: Plot) => ReactNode
  /** Passed to the <svg> so charts can attach pointer handlers. */
  svgProps?: React.SVGProps<SVGSVGElement>
  margin?: Partial<Margin>
  className?: string
}

/**
 * Measures, decides empty vs drawn, and renders the SVG. Every chart component
 * in the kit is a thin body inside one of these.
 */
export function ChartShell({
  height,
  label,
  empty,
  isEmpty = false,
  children,
  svgProps,
  margin,
  className,
}: ChartShellProps) {
  const [ref, size] = useSize<HTMLDivElement>()
  const plot = plotArea(size, height, margin)
  const ready = size.width > 0

  return (
    <div ref={ref} className={className} style={{ width: '100%', height, position: 'relative' }}>
      {isEmpty ? (
        <div
          className="w7-state"
          style={{ padding: 0, height: '100%', justifyContent: 'center', fontSize: 13 }}
        >
          {empty ?? 'Nothing to show for this range'}
        </div>
      ) : ready ? (
        <svg
          width={size.width}
          height={height}
          role="img"
          aria-label={label}
          {...svgProps}
          style={{ display: 'block', overflow: 'visible', ...svgProps?.style }}
        >
          {children(plot)}
        </svg>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ axes -- */

export interface AxisProps {
  plot: Plot
  ticks: number[]
  scale: (value: number) => number
  format: (value: number) => string
  /** Draw horizontal grid lines across the plot. */
  grid?: boolean
}

export function AxisLeft({ plot, ticks, scale, format, grid = true }: AxisProps) {
  const { margin, inner } = plot
  return (
    <g>
      {ticks.map((t) => {
        const y = margin.top + scale(t)
        const isZero = t === 0
        return (
          <g key={t}>
            {grid ? (
              <line
                x1={margin.left}
                x2={margin.left + inner.width}
                y1={y}
                y2={y}
                stroke={isZero ? 'var(--w7-zero-line)' : 'var(--w7-grid)'}
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
            ) : null}
            <text
              x={margin.left - 8}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fill="var(--w7-axis)"
              fontSize={10}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {format(t)}
            </text>
          </g>
        )
      })}
    </g>
  )
}

export interface CategoryAxisProps {
  plot: Plot
  count: number
  position: (index: number) => number
  format: (index: number) => string
  /** Show at most this many labels, thinned evenly. */
  maxLabels?: number
}

export function AxisBottom({ plot, count, position, format, maxLabels = 8 }: CategoryAxisProps) {
  const { margin, inner } = plot
  if (count === 0) return null
  const stride = Math.max(1, Math.ceil(count / maxLabels))
  const y = margin.top + inner.height + 14

  return (
    <g>
      {Array.from({ length: count }, (_, i) => i)
        // Always label the last point — a trend chart whose right edge is
        // unlabelled makes "as of when?" unanswerable.
        .filter((i) => i % stride === 0 || i === count - 1)
        .map((i) => (
          <text
            key={i}
            x={margin.left + position(i)}
            y={y}
            textAnchor="middle"
            fill="var(--w7-axis)"
            fontSize={10}
          >
            {format(i)}
          </text>
        ))}
    </g>
  )
}

/* -------------------------------------------------------------- interaction */

/**
 * Turns pointer events on the plot into a category index. Shared by every
 * category-based chart so hover behaviour is identical across the console.
 */
export function useCategoryHover(
  plot: Plot,
  count: number,
  indexAt: (x: number) => number,
): {
  hovered: number | null
  handlers: Pick<React.SVGProps<SVGRectElement>, 'onPointerMove' | 'onPointerLeave'>
  overlay: ReactNode
} {
  const [hovered, setHovered] = useState<number | null>(null)

  const onPointerMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      setHovered(count > 0 ? indexAt(x) : null)
    },
    [count, indexAt],
  )

  const onPointerLeave = useCallback(() => setHovered(null), [])

  const overlay = (
    <rect
      x={plot.margin.left}
      y={plot.margin.top}
      width={plot.inner.width}
      height={plot.inner.height}
      fill="transparent"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      style={{ cursor: 'crosshair' }}
    />
  )

  return { hovered, handlers: { onPointerMove, onPointerLeave }, overlay }
}

/** A vertical crosshair at a category position. */
export function Crosshair({ plot, x }: { plot: Plot; x: number }) {
  return (
    <line
      x1={plot.margin.left + x}
      x2={plot.margin.left + x}
      y1={plot.margin.top}
      y2={plot.margin.top + plot.inner.height}
      stroke="var(--w7-border-strong)"
      strokeWidth={1}
      shapeRendering="crispEdges"
      pointerEvents="none"
    />
  )
}
